/**
 * The booked page's standard look (booked-web.html Direction 2, Centered, the
 * check filled in the accent). Draws the behavior half's state and calls its
 * actions; holds no rule. `Ticket` and `TelegramLine` are shared with the
 * request-sent page.
 */
import type { ReactNode } from 'react';
import type { SitePhoto } from '@telify/shared';
import { CalendarMenu } from '@/components/booking/CalendarMenu';

import { partsOf, pieceRoot } from '../../contract/runtime';
import shared from '../../styles/shared.module.css';
import { CALENDAR_MENU_PARTS, type WebCalendarChoices } from '../../ui/calendarMenuParts';
import { Icon } from '../../ui/Icon';
import { Photo } from '../../ui/Photo';
import { contract, type BookedTelegramLine, type BookedTicketRow } from './contract';
import type { BookedRuntime } from './useBooked';
import css from './Booked.module.css';

const part = partsOf(contract);

/**
 * The ticket; each row's own action sits on its right (Gabriel 2026-10-22): Add to Calendar beside When, Get directions beside the place.
 * The booking page (web-account-pages D4) draws it under its own heading (`head` false), with a row's note under its words and
 * its own action for a row (Join beside an online place).
 */
export function Ticket({
  name,
  photo,
  withName,
  rows,
  calendar = null,
  head = true,
  rowAction,
}: {
  name: string;
  photo: SitePhoto | null;
  withName: string | null;
  rows: readonly (BookedTicketRow & { readonly note?: string | null })[];
  calendar?: WebCalendarChoices | null;
  head?: boolean;
  rowAction?: (row: BookedTicketRow, index: number) => ReactNode;
}) {
  return (
    <div className={`${css.ticket} ${head ? '' : css.ticketBare}`} {...part('ticket')}>
      {head && (
        <>
          <div className={css.tHead}>
            <div className={css.tThumb}>{photo && <Photo photo={photo} />}</div>
            <div>
              <div className={`${css.tName} ${css.serif}`}>{name}</div>
              {withName && <div className={css.tWith}>{withName}</div>}
            </div>
          </div>
          <div className={css.perf} aria-hidden="true" />
        </>
      )}
      <dl className={css.tRows}>
        {rows.map((r, i) => (
          <div key={r.label} {...part('row')}>
            <dt>{r.label}</dt>
            <dd>
              {r.value}
              {r.sub && <span className={css.sub}>{r.sub}</span>}
              {r.note && <span className={css.note}>{r.note}</span>}
            </dd>
            {rowAction?.(r, i) ?? (r.amount ? (
              <span className={css.amt} {...part('amount')}>
                {r.amount}
              </span>
            ) : r.directionsHref ? (
              <a className={css.dirLink} href={r.directionsHref} target="_blank" rel="noopener noreferrer" {...part('directions')}>
                <Icon name="pin" small />
                Get directions
              </a>
            ) : i === 0 && calendar ? (
              <CalendarMenu choices={calendar} onApple={calendar.download} parts={CALENDAR_MENU_PARTS} className={css.dirLink}>
                <Icon name="calplus" small />
                Add to Calendar
              </CalendarMenu>
            ) : (
              <span />
            ))}
          </div>
        ))}
      </dl>
    </div>
  );
}

export function TelegramLine({ line }: { line: BookedTelegramLine | null }) {
  if (!line) return null;
  return (
    <div className={css.tg}>
      <p className={css.tgLine} {...part('telegramLine')}>
        <Icon name="tg" />
        <span>{line.text}</span>
      </p>
      {line.chatHref && (
        <a className={`${shared.btn} ${shared.btnOutline} ${css.openChat}`} href={line.chatHref} target="_blank" rel="noopener noreferrer" {...part('openChat')}>
          Open chat
        </a>
      )}
    </div>
  );
}

export function BookedView({ state, actions }: BookedRuntime) {
  if (state.status !== 'ready') return null;
  return (
    <div className={css.c2} {...pieceRoot(contract)} {...part('page')}>
      <div className={css.head}>
        <span className={css.check} aria-hidden="true">
          <Icon name="check" />
        </span>
        <h1 className={`${css.title} ${css.serif}`} {...part('title')}>
          Booking confirmed
        </h1>
      </div>
      {/* No preparation note here: Telegram sends it after the Booked message, and a long one is out of place on this page (Gabriel 2026-10-22). */}
      <Ticket name={state.name} photo={state.photo} withName={state.withName} rows={state.rows} calendar={state.calendar && { ...state.calendar, download: actions.addToCalendar }} />
      {state.problem && (
        <p className={css.problem} role="alert" {...part('problem')}>
          {state.problem}
        </p>
      )}
      <div className={css.actions}>
        <a className={`${shared.btn} ${shared.btnPrimary}`} href={state.bookingHref} {...part('viewBooking')}>
          View my booking
        </a>
      </div>
      <TelegramLine line={state.telegram} />
    </div>
  );
}
